<script>
    import { onMount, createEventDispatcher } from 'svelte';
    import { fly, blur } from 'svelte/transition';
    import { getFocusableElements, focusOnFirst } from '@repo/config/Utils';

    let {
        tabs = [],
        thirdWidth = false,
        halfWidth = false,
        twoThirdWidth = false,
        threeQuarterWidth = false,
        showFooter = true,
        lockTab = false,
        body,
        footer = null,
        currentTab = $bindable(0),
    } = $props();

    const dispatch = createEventDispatcher();

    let showScrollTop = $state(false);
    let showScrollBottom = $state(false);
    let scrollBottomPos = $state('2rem');

    function handleCloseClick(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        dispatch('close');
    }

    function handleModalClick(e) {
        e.stopImmediatePropagation();
    }

    function handleKeyPress(e) {
        if (e.key === 'Escape') {
            dispatch('close');
        }

        const focusableContent = getFocusableElements('.modal');
        const firstFocusableElement = focusableContent[0];
        const lastFocusableElement =
            focusableContent[focusableContent.length - 1];

        if (e.key === 'Tab') {
            if (e.shiftKey) {
                // If Shift + Tab is pressed
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    e.preventDefault();
                }
            } else {
                // If Tab is pressed
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus();
                    e.preventDefault();
                }
            }
        }
    }

    function handleFocusOut(e) {
        // relatedTarget is null when focus falls to <body>
        if (!e.relatedTarget) return;

        // Check if the focus is moving outside the modal
        if (!e.currentTarget.contains(e.relatedTarget)) {
            const focusableContent = getFocusableElements('.modal');
            focusableContent[0]?.focus();
        }
    }

    onMount(async () => {
        if (document.querySelector('.form')) {
            document
                .querySelector('.form')
                .addEventListener('submit', handleCloseClick);
        }

        if (showFooter) {
            const footer = document.getElementsByClassName('modal-footer')[0];
            if (footer.childElementCount > 0) scrollBottomPos = '4.5rem';
        }

        const body = document.getElementsByClassName('modal-content')[0];

        body.addEventListener('scroll', (e) => {
            if (e.target.scrollTopMax === 0) {
                showScrollTop = false;
                showScrollBottom = false;
            } else if (e.target.scrollTop === 0) {
                showScrollTop = false;
                showScrollBottom = true;
            } else if (e.target.scrollTop === e.target.scrollTopMax) {
                showScrollTop = true;
                showScrollBottom = false;
            } else {
                showScrollTop = true;
                showScrollBottom = true;
            }
        });

        // focus the first element
        setTimeout(() => {
            focusOnFirst('.modal'); // focus within the modal
        }, 0);
    });

    function handleChangeTab(e) {
        if (lockTab) return;
        const tagName = e.target.tagName;
        if (tagName === 'BUTTON') {
            currentTab = Number(e.target.dataset.index);
        } else {
            currentTab = Number(e.target.parentElement.dataset.index);
        }
    }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
    class="modal-holder interactive"
    onclick={handleCloseClick}
    transition:blur
></div>

<div
    transition:fly={{ x: 700, duration: 500, opacity: 1 }}
    class="modal interactive"
    class:third-width={thirdWidth}
    class:half-width={halfWidth}
    class:two-third-width={twoThirdWidth}
    class:three-quarter-width={threeQuarterWidth}
    role="dialog"
    tabindex="0"
    aria-modal="true"
    aria-labelledby="modal-title"
    onclick={handleModalClick}
    onkeydown={handleKeyPress}
    onfocusout={handleFocusOut}
>
    <header class="modal-header">
        <div class="tabs">
            {#if tabs.length === 1}
                <div class="active">
                    <h2 id="modal-title">{tabs[0]}</h2>
                </div>
            {:else}
                {#each tabs as t, i}
                    <button
                        onclick={handleChangeTab}
                        data-index={i}
                        class="{currentTab === i ? 'active' : ''} interactive"
                    >
                        <span id={currentTab === i ? 'modal-title' : ''}
                            >{t}</span
                        >
                    </button>
                {/each}
            {/if}
        </div>

        <button
            onclick={handleCloseClick}
            class="close interactive"
            aria-label="Close Modal"
        >
            <i class="material-icons" aria-hidden="true">east</i>
        </button>
    </header>
    {#if showScrollTop}
        <div class="scroll-top"></div>
    {/if}
    <div class="modal-content semi-interactive">
        {@render body?.({ tab: currentTab })}
    </div>
    {#if showScrollBottom && showFooter}
        <div class="scroll-bottom" style="bottom:{scrollBottomPos};"></div>
    {/if}
    {#if showFooter}
        <div class="modal-footer">
            {@render footer?.({ tab: currentTab })}
        </div>
    {/if}
</div>

<style>
    button {
        background-color: transparent;
    }

    .modal-holder {
        display: absolute;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.75);
        z-index: 5000;
    }

    .close {
        padding: 0.5rem;
        padding-bottom: 0.2rem;
        border-radius: 10rem;
        color: inherit;
    }

    .close:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .modal {
        position: fixed;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        background-color: rgb(26, 26, 26);
        padding: 0;
        box-shadow: 0 0 2rem rgba(0, 0, 0, 0.6);
        top: 0;
        right: 0;
        width: 36rem;
        max-width: calc(100vw - (100vw - 100%));
        height: 100vh;
        color: #ddd;
        z-index: 5100;
    }

    .modal.half-width {
        width: 50vw;
    }

    .modal.two-third-width {
        width: 66vw;
    }

    .modal.three-quarter-width {
        width: 75vw;
    }

    .modal.third-width {
        width: 33vw;
    }

    .tabs {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .modal h2 {
        font-size: 1.2rem;
    }

    .modal-content {
        position: relative;
        display: flex;
        flex-direction: column;
        flex-grow: 10;
        flex-shrink: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0.5rem 1rem 0.5rem 1rem;
    }

    .modal-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid rgb(255, 206, 44);
        padding: 0.5rem 0 0.5rem 0.5rem;
        margin: 0 1rem 1rem;
    }

    .modal-header .tabs > * > * {
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.25);
    }

    .modal-header .tabs > .active > * {
        color: rgb(255, 206, 44);
    }

    .modal-header button:not(:last-of-type) {
        margin-right: 1rem;
    }

    .modal-footer {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 1rem;
        padding: 0.5rem 0 0.5rem 0.5rem;
    }

    .scroll-top {
        position: absolute;
        left: 0;
        top: 4.5rem;
        width: 100%;
        height: 2rem;
        background: radial-gradient(
            farthest-side at top center,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0) 100%
        );
        z-index: 10;
    }

    .scroll-bottom {
        position: absolute;
        left: 0;
        bottom: 4.5rem;
        width: 100%;
        height: 2rem;
        background: radial-gradient(
            farthest-side at bottom center,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0) 100%
        );
        z-index: 10;
    }
</style>
